/**
  * @class mmodQuickBar
  * @description
  * Application Quick Bar is used as addition to Application Bar in mOffice.
  * It provides functionality of Application Bar that is present in horizontal
  * mode but missing in vertical mode (used in mOffice).
  * Quick Bar displays connection status to mServer,
  * currently logged callsign and active user interface language.
  * It allows user to switch language, logout and change password.
  * Module is implemented without workflow, as a pure module named mmodQuickBar.
  * Module listens on various messages to update its appearance accordingly:
  * - mmUserLogged - used to update callsign of user
  * - mmUsersStatus - used to update operational status of user
  * - mmServerConnected/mmServerDisconnected - to update server connection icon
  * Module does not care about peering, radar or GPS, as these are not relevant for mOffice application.
  */

import mmodModule from 'mmodModule'

export default class mmodQuickBar extends mmodModule {
  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * Creates an instance of the class.
  * @param {string} [id] - ID under which the object will listen for messages
  * @param {string} [cfg] - object initial configuration
  * @param {string} [cfg_schema] - additional schema properties from derived classes
  * @param {string} [dependencies] - additional dependencies from derived classes
  */
  constructor(id, cfg, cfg_schema, dependencies) {
    dependencies = _.union([/* put module specific dependencies here */], dependencies)
    cfg_schema = _.extend({
      init: {
        type: 'object',
        default: { type: 'dock', side: 'top', height: 42, order: 3 },
        description: 'Initial window setup; same structure as required by `mmWindowSet`',
      },
      behaviors: {
        description: 'comma separated list of behaviors available for switching',
        type: 'array',
        items: { type: 'string', enum: ['desktop', 'dashboard', 'tablet'] },
        default: ['desktop', 'dashboard', 'tablet'],
      },
      languages: {
        description: 'list of displayed languages',
        type: 'array',
        items: { type: 'string' },
        default: [],
      },
    }, cfg_schema)
    if(require('msysFS').device()=='phone') cfg.disabled = true
    super(id, cfg, cfg_schema, dependencies)
  } // constructor

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * initialization of GUI components
  */
  initWindow() {
    this.loadForm(require('./mmodQuickBar.json'))
    this.el.forAll('@behavior', el=>el.options = this.config.behaviors)
    this.el.forAll('@language', el=>el.options = this.config.languages)
    this.mmStart()
  } // initWindow

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * Request to set language
  * @param {object} sender - message sender
  * @param {object} params - message parameters
  * @param {string} [params.lang] - desired language in 2 character ISO 639-1
  */
  mmSetLanguage(sender, params) {
    this.el.forAll('@language', el=>el.value = params.lang)
    super.mmSetLanguage(sender, params)
  } // mmSetLanguage

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * Sends by &lt;mele-input> to parent module when input value changed
  * @param {object} sender - message sender
  * @param {object} params - message parameters
  * @param {string} [params.holder] - key attribute of input's parent container
  * @param {string} params.input - key attribute of the input element changed
  * @param {string} [params.value] - new value
  * @param {string} [params.old] - old value
  * @param {boolean} [params.invalid] - if true, indicates the value is not according to given format
  * @param {boolean} [params.interactive] - indicates if this was user activity
  */
  mmValueChanged(sender, params) {
    if(!params.interactive) return
    const input=params.input
    const value = params.value
    if(!value) return
    if(input=='status') {
      this.publish('msysUser', 'mmUserStatus', { status: value })
    }else if(input=='theme') {
      this.publish('#ALLWITHME', 'mmSetTheme', { theme: value })
    }else if(input=='behavior') {
      this.publish('#ALLWITHME', 'mmSetBehavior', { behavior: value })
    }else if(input=='language') {
      this.publish('#ALLWITHME', 'mmSetLanguage', { lang: value })
    }
  } // mmValueChanged

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * Sends by &lt;mele-button> to parent module when a button is clicked
  * @param {object} sender - message sender
  * @param {object} params - message parameters
  * @param {string} [params.holder] - key attribute of button's parent container
  * @param {string} params.button - key attribute of the button
  * @param {integer} [params.count] - number of times button was clicked
  * @param {boolean} [params.long] - shows if press was long or not
  */
  mmButtonClicked(sender, params) {
    const button = params.button
    if(button=='sign_in') {
      this.publish('msysUser', 'mmUserLogin', { callsign: this.el.find('@callsign').value })
    }else if(button=='user_id') {
      this.el.find('@user_id').modalOpen({
        key: 'user_modal',
        position: 'below',
        outclick: ':modal.cancel',
        items: [
          { tag: 'mele-button', key: 'changepwd', label: 'Change password' },
          { tag: 'mele-button', key: 'logout', label: 'Log out' },
        ],
      })
    }else if(button=='changepwd') {
      this.modalClose('user_modal')
      this.publish('mmodAppBar', 'mmListSelected', { holder: 'status', selected: 'changepwd' })
    }else if(button=='logout') {
      // methid in mmodModule
      this.logoutUser()
    }
  } // mmButtonClicked

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * information on changed user authorization; user_id is empty if not logged
  * @param {object} sender - message sender
  * @param {object} params - message parameters
  * @param {string} [params.app_id] - unique application ID
  * @param {integer} [params.user_id] - user's unique ID
  * @param {string} [params.login] - user's login name
  * @param {string} [params.callsign] - user's callsign
  * @param {object} [params.roles] - roles assigned to user
  * @param {string} [params.privatekey] - user's private key encrypted by his password
  * @param {string} [params.error] - error message if login failed
  */
  mmUserLogged(sender, params) {
    if(this.user_id && !params.user_id) {
      if(typeof nw !== 'undefined' && nw?.global) {
        nw.global.disable_autologin = true
      }
      setTimeout(()=>{
        this.el.ownerDocument.defaultView.location.reload()
      }, 100)
    }

    super.mmUserLogged(sender, params)
    this.el.forAll('@callsign', el=>el.value = params.callsign)
  } // mmUserLogged

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * notification on changed user status
  * @param {object} sender - message sender
  * @param {object} params - message parameters
  * @param {string} [params.app_id] - unique application ID
  * @param {string} params.status - new status
  * @param {string} [params.info] - additional info about status change
  */
  mmUserStatus(sender, params) {
    super.mmUserStatus(sender, params)
    this.el.forAll('@status', el=>el.setProperty('value', params.status, true))
  } // mmUserStatus

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * msysDomainClient informs all listeners that connection to mServer is established
  * @param {object} sender - message sender
  * @param {object} params - message parameters
  * @param {string} [params.app_id] - app_id of the server
  * @param {string} params.url - URL of connected server
  * @param {string} [params.ident] - Identity string of server
  * @param {string} [params.client_ip] - IP of the client as recognized by the server
  */
  mmServerConnected(sender, params) {
    this.el.find('@online').value = 1
    this.el.find('@online').title = params.url
  } // mmServerConnected

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * msysDomainClient informs all listeners that connection to mServer has dropped
  * @param {object} sender - message sender
  * @param {object} params - message parameters
  * @param {string} [params.reason] - reason for close
  * @param {string} params.url - URL of disconnected server
  */
  mmServerDisconnected(sender, params) {
    this.el.find('@online').value = 0
  } // mmServerDisconnected

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * Request to change application color theme
  * @param {object} sender - message sender
  * @param {object} params - message parameters
  * @param {string} [params.theme] - desired color theme
  */
  mmSetTheme(sender, params) {
    this.el.forAll('@theme', el=>el.value = params.theme)
    super.mmSetTheme(sender, params)
  } // mmSetTheme

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * Request to change application behavior
  * @param {object} sender - message sender
  * @param {object} params - message parameters
  * @param {string} [params.behavior] - desired behavior
  */
  mmSetBehavior(sender, params) {
    const behavior = super.mmSetBehavior(sender, params)
    const elBehavior = this.el.find('@behavior')
    if(elBehavior && behavior=='desktop') elBehavior.value = behavior
    if(this.previous_behavior!=behavior) {
      this.publish('msysWM', 'mmWindowSet', behavior=='desktop' ? this.config.init : { type: 'none' })
    }
    this.previous_behavior = behavior
  } // mmSetBehavior
} // mmodQuickBar
